import { User, UserRole } from '../types';

/** Reserved businesses.short_name for platform roles; must match provisioning SQL. */
export const PLATFORM_SYSTEM_BUSINESS_SHORT_NAME = '_aacore_system';

/**
 * AACore platform operator: Admin role on the internal sentinel business only.
 * Does not include tenant "Admin" roles on customer businesses.
 */
export const isPlatformSystemAdmin = (user: User | null): boolean => {
  if (!user?.roles?.length) return false;
  return user.roles.some(
    (role) =>
      role.role === 'Admin' &&
      role.isActive &&
      role.businessRoleActive !== false &&
      role.businessShortName === PLATFORM_SYSTEM_BUSINESS_SHORT_NAME
  );
};

export const getUserPrimaryRole = (user: User, businessId?: string): string | null => {
  if (!user.roles || user.roles.length === 0) {
    return null;
  }

  // Priority order: Business Owner > Professional > Custom roles
  const rolePriority = {
    'Business Owner': 3,
    'Professional': 2
  };

  // Filter for truly active roles (both user role and business role must be active)
  const activeRoles = user.roles.filter(role => 
    role.isActive &&
    role.businessRoleActive !== false &&
    (!businessId || role.businessId === businessId)
  );
  
  if (activeRoles.length === 0) {
    return null;
  }

  // Find the highest priority role
  const primaryRole = activeRoles.reduce((highest, current) => {
    const currentPriority = rolePriority[current.role as keyof typeof rolePriority] || 1; // Custom roles get priority 1
    const highestPriority = rolePriority[highest.role as keyof typeof rolePriority] || 1;
    
    return currentPriority > highestPriority ? current : highest;
  });

  return primaryRole.role;
};

/**
 * Check if a user is a Business Owner for a specific business
 */
export const isBusinessOwnerForBusiness = (user: User | null, businessId: string): boolean => {
  if (!user || !user.roles) return false;
  
  return user.roles.some(role => 
    role.businessId === businessId && 
    role.role === 'Business Owner' && 
    role.isActive &&
    role.businessRoleActive !== false
  );
};

/**
 * Check if a user is a Business Owner for any business
 */
export const isBusinessOwner = (user: User | null): boolean => {
  if (!user || !user.roles) return false;
  
  return user.roles.some(role => 
    role.role === 'Business Owner' && 
    role.isActive &&
    role.businessRoleActive !== false
  );
};

/**
 * Whether the user may open the business dashboard Customers page for a business.
 * Business owners always have access.
 */
export const canAccessCustomersPage = (user: User | null, businessId?: string): boolean => {
  if (!user?.roles?.length || !businessId) return false;

  const role = user.roles.find(
    (r) =>
      r.businessId === businessId &&
      r.isActive &&
      r.businessRoleActive !== false &&
      r.role !== 'CUSTOMER'
  );

  if (!role) return false;
  if (role.role === 'Business Owner') return true;
  return role.canAccessCustomers !== false;
};

/**
 * Whether the user may view full customer phone numbers for a business.
 * Business owners always have access. Defaults to true when unset.
 */
export const canViewFullCustomerPhone = (user: User | null, businessId?: string): boolean => {
  if (!user?.roles?.length || !businessId) return false;

  const role = user.roles.find(
    (r) =>
      r.businessId === businessId &&
      r.isActive &&
      r.businessRoleActive !== false &&
      r.role !== 'CUSTOMER'
  );

  if (!role) return false;
  if (role.role === 'Business Owner') return true;
  return role.canViewFullPhoneNumber !== false;
};

export const isProfessional = (user: User): boolean => {
  if (!user.roles || user.roles.length === 0) return false;
  
  // Filter for truly active roles (both user role and business role must be active)
  const activeRoles = user.roles.filter(role => 
    role.isActive && role.businessRoleActive !== false
  );
  
  // Check for professional permissions
  const professionalPermissions = [
    'manage_schedule', 'view_own_appointments', 'manage_own_services',
    'manage_availability', 'view_schedule', 'can_perform_services'
  ];
  
  return activeRoles.some(role => 
    role.permissions?.some(permission => 
      professionalPermissions.includes(permission)
    )
  );
};

export const isCustomer = (user: User): boolean => {
  if (!user.roles || user.roles.length === 0) return true;
  
  // Filter for truly active roles (both user role and business role must be active)
  const activeRoles = user.roles.filter(role => 
    role.isActive && role.businessRoleActive !== false
  );
  
  if (activeRoles.length === 0) return true;
  
  // Check for explicit dashboardType
  if (activeRoles.some(role => role.dashboardType === 'customer')) {
    return true;
  }
  
  // If user has any permissions, they're not a customer
  const hasAnyPermissions = activeRoles.some(role => 
    role.permissions && role.permissions.length > 0
  );
  
  return !hasAnyPermissions;
};

export const getDashboardRoute = (user: User): string => {
  if (!user.roles || user.roles.length === 0) {
    return '/dashboard/customer';
  }

  // Filter for truly active roles (both user role and business role must be active)
  const activeRoles = user.roles.filter(role =>
    role.isActive && role.businessRoleActive !== false
  );

  if (activeRoles.length === 0) {
    return '/dashboard/customer';
  }

  if (isPlatformSystemAdmin(user)) {
    return '/dashboard/platform';
  }

  // Check if user can perform services (only if they have active roles)
  if (user?.canPerformServices && activeRoles.length > 0) {
    return '/dashboard/business';
  }

  // First, check if any role has an explicit dashboardType
  for (const role of activeRoles) {
    if (role.dashboardType) {
      switch (role.dashboardType) {
        case 'business':
          return '/dashboard/business';
        case 'customer':
          return '/dashboard/customer';
      }
    }
  }

  return getDashboardRouteByPermissions(activeRoles);
};

// Function to determine dashboard based on permissions across all active roles
const getDashboardRouteByPermissions = (activeRoles: UserRole[]): string => {
  // Collect all permissions from all active roles
  const allPermissions = new Set<string>();
  
  for (const role of activeRoles) {
    if (role.permissions && role.permissions.length > 0) {
      role.permissions.forEach(permission => allPermissions.add(permission));
    }
  }

  // If no permissions at all, default to customer dashboard
  if (allPermissions.size === 0) {
    return '/dashboard/customer';
  }

  const permissions = Array.from(allPermissions);

  // Define permission patterns that indicate which dashboard to use
  const businessManagementPermissions = [
    'manage_services', 'manage_roles', 'manage_professionals', 
    'manage_settings', 'manage_branding', 'view_reports', 'manage_testimonials',
    'manage_gift_cards', 'manage_appointments'
  ];

  const professionalPermissions = [
    'manage_schedule', 'view_own_appointments', 'manage_own_services',
    'manage_availability', 'view_schedule', 'can_perform_services'
  ];

  // Check if user has business management capabilities
  const hasBusinessPermissions = businessManagementPermissions.some(permission => 
    permissions.includes(permission)
  );

  // Check if user has professional capabilities
  const hasProfessionalPermissions = professionalPermissions.some(permission => 
    permissions.includes(permission)
  );

  // Determine dashboard based on permission patterns
  // All users with any business or professional permissions go to business dashboard
  if (hasBusinessPermissions || hasProfessionalPermissions) {
    return '/dashboard/business';
  }

  // Default to customer dashboard if no specific permissions match
  return '/dashboard/customer';
};

// Check if user has a specific permission
export const hasPermission = (user: User, permission: string, businessId?: string): boolean => {
  if (!user.roles || user.roles.length === 0) {
    return false;
  }

  const relevantRoles = businessId 
    ? user.roles.filter(role => 
        role.businessId === businessId && 
        role.isActive && 
        role.businessRoleActive !== false
      )
    : user.roles.filter(role => 
        role.isActive && 
        role.businessRoleActive !== false
      );

  return relevantRoles.some(role => 
    role.permissions && role.permissions.includes(permission)
  );
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (user: User, permissions: string[], businessId?: string): boolean => {
  return permissions.some(permission => hasPermission(user, permission, businessId));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (user: User, permissions: string[], businessId?: string): boolean => {
  return permissions.every(permission => hasPermission(user, permission, businessId));
};

// Get all permissions for a user in a specific business
export const getUserPermissions = (user: User, businessId?: string): string[] => {
  if (!user.roles || user.roles.length === 0) {
    return [];
  }

  const relevantRoles = businessId 
    ? user.roles.filter(role => 
        role.businessId === businessId && 
        role.isActive && 
        role.businessRoleActive !== false
      )
    : user.roles.filter(role => 
        role.isActive && 
        role.businessRoleActive !== false
      );

  const allPermissions = new Set<string>();
  relevantRoles.forEach(role => {
    if (role.permissions) {
      role.permissions.forEach(permission => allPermissions.add(permission));
    }
  });

  return Array.from(allPermissions);
};

/**
 * Check if a user has any active business roles
 * A user has active business roles if they have at least one role where:
 * - The user role is active (isActive: true)
 * - The business role is active (businessRoleActive !== false)
 * - The role has a businessId (indicating it's actually a business role)
 */
export const hasActiveBusinessRoles = (user: User | null): boolean => {
  if (!user || !user.roles || user.roles.length === 0) {
    return false;
  }

  return user.roles.some(role => {
    const isUserRoleActive = role.isActive;
    const isBusinessRoleActive = role.businessRoleActive !== false;
    const hasBusinessId = !!role.businessId;
    const isSystemShell = role.businessShortName === PLATFORM_SYSTEM_BUSINESS_SHORT_NAME;
    return (
      isUserRoleActive &&
      isBusinessRoleActive &&
      hasBusinessId &&
      !isSystemShell
    );
  });
}; 